import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Typography } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import Progress from '../item/progress'

export default function BarLine(props) {
    const [categoryDates, setCategoryDates] = useState([]);
    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const [seriesMax, setSeriesMax] = useState(0);
    const [riskPoints, setRiskPoints] = useState([]);
    const [isNoData, setIsNoData] = useState(false);

    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    // チャート情報を格納
    const setChartData = (date_ary, data_x, data_y) => {
        setCategoryDates(date_ary);
        setXData(data_x);
        setYData(data_y);

        if (data_x.length > 0) {
            // 土砂災害発生リスク、最大値を取得
            setSeriesMax(props.limitVal + 1)
            if (data_x.length > 0 && data_x.reduce(aryMax) > props.limitVal) {
                setSeriesMax(data_x.reduce(aryMax))
            }
            setRiskPoints(props.getPoints(date_ary, data_x))
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // データ更新が完了したらローディング終了
    useEffect(() => {
        if (yData !== null) {
            const timer = setTimeout(() => {
                loadingEnd();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [yData]);

    useEffect(() => {
        if (props.dateArray === null
            || props.xDatetime === null
            || props.yDatetime === null)
        {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.xDatetime && props.xDatetime.length > 0
            && props.yDatetime && props.yDatetime.length > 0
        ) {
            setChartData(props.dateArray, props.xDatetime, props.yDatetime)
        }
        setIsNoData(false)
    }, [props.dateArray, props.xDatetime, props.yDatetime]);

    useEffect(() => {
        if (props.dateArray === null ||
            props.xDatetime === null || 
            props.yDatetime === null
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0 &&
            props.xDatetime && props.xDatetime.length > 0 &&
            props.yDatetime && props.yDatetime.length > 0
        ) {
            setChartData(props.dateArray, props.xDatetime, props.yDatetime)
        }
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const series = useMemo(() => {
        return [
            {
                name: props.seriesNameX,
                type: 'line',
                data: xData
            },
            {
                name: props.seriesNameY,
                type: 'column',
                data: yData
            },
        ];
    }, [xData, yData]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
                offsetY: 0
            },
            type: "line",
            stacked: false
        },
        // ポイントの数値表示
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0, 1],
            offsetY: props.dataLabelsOffsetY ?? -10,
            style: {
                fontSize: '8px',
                colors: ['white', 'white']
            }
        },
        colors: props.colors,
        stroke: {
            width: [2, 0],
            colors: props.strokeColors
        },
        annotations: {
            yaxis: props.annotations ? [props.annotations] : [],
            xaxis: [
                {
                    x: categoryDates[props.chooseNum],
                    borderColor: '#0000FF',
                    borderWidth: 1,
                    strokeDashArray: 4,
                    label: {
                        show: false
                    }
                }
            ],
            points: riskPoints
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    position: 'top' // top, center, bottom
                },
                borderRadiusApplication: 'around'
            }
        },
        xaxis: {
            categories: categoryDates,
            labels: {
                rotate: -55,
                rotateAlways: true,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '10px'
                }
            }
        },
        yaxis: [
            {
                opposite: true,
                min: seriesMax * -1,
                max: seriesMax,
                seriesName: props.seriesNameX,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                },
                lines: {
                    show: false
                }
            },
            {
                seriesName: props.seriesNameY,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                },
                lines: {
                    show: false
                }
            }
        ],
        tooltip: {
            shared: props.shared,
            intersect: props.intersect,
            x: {
                show: false
            },
            y: {
                formatter: props.format
            }
        },
        grid: {
            show: false, // 全体の罫線を消す
            padding: {
                left: 25,
                right: 2,
                top: -10,
                bottom: props.bottom
            }
        },
        legend: {
            show: false,
            height: 0,
        }
    }), [riskPoints, categoryDates, seriesMax, props.chooseNum]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            {!props.isReadyFlg ?
                <Progress />
                :
                isNoData ?
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'white',
                        }}
                    >
                        対象データがありません
                    </Typography>
                    :
                    <Chart
                        options={options}
                        series={series}
                        type="bar"
                        height="100%"
                        width="100%"
                        style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                    />
            }
        </>
    );
}